import { api } from '@web/api/client'
import type {
  AppBinding,
  AppDefinition,
  BridgeConfig,
  SessionConfig,
  SessionHistoryItem,
  SessionRecord,
} from '@web/api/types'
import {
  AppOverview,
  GalleryGrid,
  GallerySkeleton,
} from '@web/components/AppGallery'
import { AppPane } from '@web/components/AppPane'
import { AppStartup } from '@web/components/AppStartup'
import { CanopyMark } from '@web/components/CanopyMark'
import { GitHubConnections } from '@web/components/GitHubConnections'
import { IdentityGate } from '@web/components/Identity'
import { MobileHome } from '@web/components/mobile/MobileHome'
import { SessionsScreen } from '@web/components/mobile/SessionsScreen'
import { SettingsSheet } from '@web/components/mobile/SettingsScreen'
import { CircleButton, TopBar } from '@web/components/mobile/TopBar'
import { NewSessionDialog } from '@web/components/NewSessionDialog'
import { OpeningState } from '@web/components/OpeningState'
import {
  launchSelection,
  launchSettings,
  ProviderSettings,
} from '@web/components/ProviderSettings'
import { SessionCost } from '@web/components/SessionCost'
import { SessionView } from '@web/components/SessionView'
import { Sidebar } from '@web/components/Sidebar'
import { Icon } from '@web/components/ui/icons'
import { appIcon } from '@web/lib/appIcon'
import { DocumentTitle } from '@web/lib/documentTitle'
import { useNarrow } from '@web/lib/useNarrow'
import {
  appConfiguration,
  useAppPreference,
} from '@web/state/app-configuration'
import { ConfigContext } from '@web/state/config-context'
import {
  IdentityProvider,
  navigateWorkspace as navigate,
  navigateBack,
  useIdentity,
} from '@web/state/identity'
import { getSessionStore } from '@web/state/session-store'
import {
  adoptLaunch,
  beginLaunch,
  markLaunch,
} from '@web/state/startup-telemetry'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import '@web/app-launcher.css'
import '@web/identity.css'
import '@web/startup.css'
import '@web/session-layout.css'
import '@web/session-glass.css'
import '@web/components/mobile/mobile.css'

export function App() {
  return (
    <IdentityProvider>
      <IdentityGate>
        <AuthenticatedWorkspace />
      </IdentityGate>
    </IdentityProvider>
  )
}

function AuthenticatedWorkspace() {
  const identity = useIdentity()
  return <Workspace key={identity.workspaceKey} />
}

function Workspace() {
  const identity = useIdentity()
  const [url, setUrl] = useState(() => new URL(location.href))
  const archived = url.searchParams.get('archived') === '1'
  const toggleArchived = () => {
    const next = new URL(url)
    if (archived) {
      next.searchParams.delete('archived')
      if (
        sessions.some(
          (session) =>
            session.id === next.pathname.split('/').at(-1) &&
            session.status === 'archived',
        )
      ) {
        next.pathname = next.pathname.replace(/\/sessions\/[^/]+$/, '')
      }
    } else next.searchParams.set('archived', '1')
    navigate(`${next.pathname}${next.search}`)
  }
  const historyMode = useRef(archived)
  historyMode.current = archived
  const [apps, setApps] = useState<AppDefinition[]>([])
  const [serverConfig, setConfig] = useState<BridgeConfig | null>(null)
  const config = useMemo(
    () =>
      serverConfig
        ? {
            ...serverConfig,
            defaults: {
              ...serverConfig.defaults,
              permission_mode:
                identity.state?.current_user?.default_permission_mode ?? 'auto',
            },
          }
        : null,
    [serverConfig, identity.state?.current_user?.default_permission_mode],
  )
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [error, setError] = useState('')
  const [settingsError, setSettingsError] = useState('')
  const [launching, setLaunching] = useState<string | null>(null)
  const [sessions, setSessions] = useState<SessionHistoryItem[]>([])
  const [sessionCursor, setSessionCursor] = useState<string | null>(null)
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [historyError, setHistoryError] = useState('')
  const [historyRefreshing, setHistoryRefreshing] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const historyRequest = useRef<Promise<void> | null>(null)
  const loadMoreRequest = useRef(false)
  const loadedOlderSessionIds = useRef(new Set<string>())
  const sessionCursorRef = useRef<string | null>(null)
  const loadedHistoryMode = useRef<boolean | null>(null)
  const active = useRef(true)
  useEffect(() => {
    active.current = true
    const change = () => setUrl(new URL(location.href))
    window.addEventListener('popstate', change)
    return () => {
      active.current = false
      window.removeEventListener('popstate', change)
    }
  }, [])
  const refreshApps = useCallback(async () => {
    setCatalogLoading(true)
    setError('')
    try {
      // Host-only entrypoints can render as soon as the catalog is available.
      const settings = api
        .config()
        .then((value) => {
          if (active.current) {
            setConfig(value)
            setSettingsError('')
          }
        })
        .catch((failure: Error) => {
          if (active.current) setSettingsError(failure.message)
        })
      const catalog = await api.apps()
      if (active.current) {
        setApps(catalog)
      }
      void settings
    } catch (e) {
      if (active.current) setError((e as Error).message)
    } finally {
      if (active.current) setCatalogLoading(false)
    }
  }, [])
  const refresh = useCallback(() => {
    if (historyRequest.current) return historyRequest.current
    if (!active.current || historyMode.current !== archived)
      return Promise.resolve()
    setHistoryRefreshing(true)
    const pending = Promise.resolve().then(async () => {
      const startedAt = Date.now()
      try {
        const page = await api.sessions(undefined, archived)
        if (active.current && historyMode.current === archived) {
          const preserveLoadedPages = loadedHistoryMode.current === archived
          loadedHistoryMode.current = archived
          setSessions((current) => {
            const merged = new Map<string, SessionHistoryItem>()
            if (preserveLoadedPages) {
              for (const record of current) {
                if (
                  loadedOlderSessionIds.current.has(record.id) &&
                  (record.status === 'archived') === archived
                )
                  merged.set(record.id, record)
                else if (
                  (record.status === 'archived') === archived &&
                  record.created_at * 1000 >= startedAt
                )
                  merged.set(record.id, record)
              }
            }
            for (const record of page.sessions) merged.set(record.id, record)
            return [...merged.values()].sort(
              (left, right) =>
                right.updated_at - left.updated_at ||
                right.id.localeCompare(left.id),
            )
          })
          sessionCursorRef.current = page.nextCursor
          setSessionCursor(page.nextCursor)
          setHistoryLoaded(true)
          setHistoryError('')
        }
      } catch (e) {
        if (active.current) setHistoryError((e as Error).message)
      } finally {
        if (historyRequest.current === pending) historyRequest.current = null
        if (active.current) setHistoryRefreshing(false)
      }
    })
    historyRequest.current = pending
    return pending
  }, [archived])
  const loadMoreSessions = useCallback(async () => {
    const cursor = sessionCursorRef.current
    if (
      !cursor ||
      loadedHistoryMode.current !== archived ||
      loadMoreRequest.current
    )
      return
    loadMoreRequest.current = true
    setLoadingMoreSessions(true)
    try {
      const page = await api.sessions(undefined, archived, cursor)
      if (
        active.current &&
        historyMode.current === archived &&
        sessionCursorRef.current === cursor
      ) {
        setSessions((current) => {
          const merged = new Map<string, SessionHistoryItem>()
          for (const record of current) merged.set(record.id, record)
          for (const record of page.sessions) {
            loadedOlderSessionIds.current.add(record.id)
            merged.set(record.id, record)
          }
          return [...merged.values()].sort(
            (left, right) =>
              right.updated_at - left.updated_at ||
              right.id.localeCompare(left.id),
          )
        })
        sessionCursorRef.current = page.nextCursor
        setSessionCursor(page.nextCursor)
        setHistoryError('')
      }
    } catch (e) {
      if (active.current) setHistoryError((e as Error).message)
    } finally {
      loadMoreRequest.current = false
      if (active.current) setLoadingMoreSessions(false)
    }
  }, [archived])
  useEffect(() => {
    void refreshApps()
  }, [refreshApps])
  useEffect(() => {
    void refresh()
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    const timer = setInterval(refreshWhenVisible, 15000)
    window.addEventListener('focus', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [refresh])
  const match = url.pathname.match(
    /^\/apps\/([^/]+)(?:\/sessions\/([^/]+))?\/?$/,
  )
  const appId = match ? decodeURIComponent(match[1]!) : null
  const sessionId = match?.[2] ?? null
  // Phone-only screens; on a desktop these fall back to the workspace home.
  const screen: 'sessions' | 'settings' | null =
    url.pathname === '/sessions'
      ? 'sessions'
      : url.pathname === '/settings'
        ? 'settings'
        : null
  const [linkedSession, setLinkedSession] = useState<{
    app: AppDefinition
    record: SessionRecord
  } | null>(null)
  const [sessionRecord, setSessionRecord] = useState<SessionRecord | null>(null)
  const [linkedError, setLinkedError] = useState('')
  const updateSessionTitle = useCallback((updated: SessionRecord) => {
    const title = {
      title: updated.title,
      title_manual: updated.title_manual,
      title_provisional: updated.title_provisional,
    }
    setSessions((items) =>
      items.map((item) =>
        item.id === updated.id ? { ...item, ...title } : item,
      ),
    )
    setLinkedSession((current) =>
      current?.record.id === updated.id
        ? { ...current, record: { ...current.record, ...title } }
        : current,
    )
    setSessionRecord((current) =>
      current?.id === updated.id ? { ...current, ...title } : current,
    )
  }, [])
  const missingCatalogApp = !!appId && !apps.some((item) => item.id === appId)
  useEffect(() => {
    let current = true
    if (
      !sessionId ||
      !appId ||
      !historyLoaded ||
      catalogLoading ||
      missingCatalogApp
    )
      return
    setSessionRecord((existing) =>
      existing?.id === sessionId ? existing : null,
    )
    void api.session(sessionId).then(
      (record) => {
        if (!current) return
        if (record.app_id !== appId) {
          setHistoryError('This session belongs to a different app.')
          return
        }
        setSessionRecord(record)
        setHistoryError('')
        loadedOlderSessionIds.current.add(record.id)
        setSessions((items) =>
          items.some((item) => item.id === record.id)
            ? items
            : [...items, record].sort(
                (left, right) =>
                  right.updated_at - left.updated_at ||
                  right.id.localeCompare(left.id),
              ),
        )
      },
      (failure: Error) => {
        if (current) setHistoryError(failure.message)
      },
    )
    return () => {
      current = false
    }
  }, [sessionId, appId, historyLoaded, catalogLoading, missingCatalogApp])
  useEffect(() => {
    let active = true
    setLinkedSession(null)
    setLinkedError('')
    if (sessionId && appId && missingCatalogApp && !catalogLoading)
      void Promise.all([
        api.sessionApp(sessionId),
        api.session(sessionId),
      ]).then(
        ([linkedApp, record]) => {
          if (!active) return
          if (linkedApp.id !== appId || record.app_id !== appId) {
            setLinkedError('This session belongs to a different app.')
            return
          }
          setLinkedSession({ app: linkedApp, record })
        },
        (failure: Error) => {
          if (active) setLinkedError(failure.message)
        },
      )
    return () => {
      active = false
    }
  }, [sessionId, appId, missingCatalogApp, catalogLoading])
  const app =
    apps.find(
      (item) =>
        item.id === appId &&
        (!url.searchParams.get('package') ||
          item.digest === url.searchParams.get('package')),
    ) ??
    apps.find((item) => item.id === appId) ??
    (linkedSession?.record.id === sessionId && linkedSession.app.id === appId
      ? linkedSession.app
      : undefined)
  const currentSession =
    sessionRecord?.id === sessionId
      ? sessionRecord
      : linkedSession?.record.id === sessionId && linkedSession.app.id === appId
        ? linkedSession.record
        : null
  const pageTitle = url.searchParams.has('connections')
    ? 'GitHub connections'
    : screen === 'sessions'
      ? 'Sessions'
      : screen === 'settings'
        ? 'Settings'
        : appId && app?.launchMode === 'overview'
          ? app.title
          : appId && app
            ? sessionId
              ? !historyLoaded
                ? `Loading ${app.title} session`
                : currentSession?.status === 'created' &&
                    currentSession.migration_status !== 'diagnostic' &&
                    config
                  ? `Set up ${app.title}`
                  : currentSession
                    ? `${currentSession.title} · ${app.title}`
                    : historyError
                      ? 'Session unavailable'
                      : `${app.title} session unavailable`
              : url.searchParams.has('binding') &&
                  app.launchMode === 'workspace'
                ? app.title
                : `Start ${app.title}`
            : appId && catalogLoading
              ? sessionId
                ? 'Loading session'
                : 'Loading agent'
              : appId && sessionId && missingCatalogApp
                ? linkedError
                  ? 'Session unavailable'
                  : 'Opening shared session'
                : appId
                  ? 'Agent unavailable'
                  : catalogLoading
                    ? 'Loading agents'
                    : error || historyError
                      ? 'Agents unavailable'
                      : 'Agents'
  const [historyMinimized] = useAppPreference(
    app?.id ?? 'workspace',
    'history-minimized',
    app ? appConfiguration(app).sessions.history_minimized : false,
  )
  const narrow = useNarrow()
  const historyCollapsed = historyMinimized
  const select = (id: string) => {
    const record = sessions.find((item) => item.id === id)
    if (record) {
      setHistoryOpen(false)
      navigate(
        `/apps/${record.app_id}/sessions/${id}${archived ? '?archived=1' : ''}`,
      )
    }
  }
  const launch = async (selected: AppDefinition, fresh = false) => {
    setLaunching(selected.id)
    setError('')
    const bindingKey = `bridge.app.${identity.state!.current_user!.id}.${identity.state!.current_org!.id}.${selected.id}.${selected.digest ?? selected.version}.binding`
    try {
      if (selected.launchMode === 'workspace' && !fresh) {
        const remembered = localStorage.getItem(bindingKey)
        if (remembered) {
          try {
            const previous = await api.appBinding(remembered)
            if (!active.current) return
            if (
              previous.digest !== selected.digest ||
              previous.appId !== selected.id
            )
              throw new Error('The app version changed.')
            navigate(
              `/apps/${selected.id}?binding=${encodeURIComponent(remembered)}&package=${selected.digest}`,
            )
            return
          } catch {
            localStorage.removeItem(bindingKey)
          }
        }
      }
      if (!active.current) return
      beginLaunch('app-launch')
      const result = await api.launchApp(selected.id, {
        packageDigest: selected.digest,
      })
      markLaunch('create_response')
      if (!active.current) return
      if (result.session) {
        adoptLaunch(result.session.id)
        navigate(`/apps/${result.session.app_id}/sessions/${result.session.id}`)
      } else if (result.binding) {
        localStorage.setItem(bindingKey, result.binding.id)
        navigate(
          `/apps/${selected.id}?binding=${result.binding.id}&package=${selected.digest}`,
        )
      } else
        throw new Error('The app did not return a workspace or conversation.')
      void refresh()
    } catch (e) {
      if (active.current) setError((e as Error).message)
    } finally {
      if (active.current) setLaunching(null)
    }
  }
  return (
    <ConfigContext.Provider value={config}>
      <DocumentTitle>{pageTitle}</DocumentTitle>
      {!narrow && (
        <SettingsSheet
          open={screen === 'settings'}
          onClose={() => navigateBack('/')}
        />
      )}
      {narrow ? (
        <div className="app-space m-shell">
          {url.searchParams.has('connections') ? (
            <div className="m-screen">
              <TopBar
                title="GitHub connections"
                onBack={() => navigateBack('/settings')}
              />
              <div className="app-space-main">
                <GitHubConnections
                  userId={
                    url.searchParams.get('connections') === 'me'
                      ? undefined
                      : url.searchParams.get('connections') || undefined
                  }
                />
              </div>
            </div>
          ) : screen === 'sessions' ? (
            <SessionsScreen
              archived={archived}
              onToggleArchived={toggleArchived}
              apps={apps}
              sessions={sessions}
              appId={url.searchParams.get('app') ?? undefined}
              config={config}
              refreshing={historyRefreshing}
              error={historyError}
              hasMoreSessions={
                loadedHistoryMode.current === archived && !!sessionCursor
              }
              loadingMoreSessions={loadingMoreSessions}
              onLoadMoreSessions={loadMoreSessions}
              onRenamed={(record) =>
                setSessions((items) =>
                  items.map((item) => (item.id === record.id ? record : item)),
                )
              }
              onChanged={refresh}
            />
          ) : appId && app?.launchMode === 'overview' ? (
            <div className="m-screen">
              <TopBar
                title={app.title}
                subtitle={app.description}
                left={
                  <img
                    className="m-topbar-app-icon"
                    src={appIcon(app.iconUrl, 64)}
                    alt=""
                  />
                }
                onBack={() => navigateBack('/')}
                right={
                  <CircleButton
                    icon="grid"
                    tone="ghost"
                    label="All agents"
                    onClick={() => navigate('/')}
                  />
                }
              />
              <div className="app-space-main">
                <AppOverview app={app} />
              </div>
            </div>
          ) : appId && app ? (
            <AppSpace
              key={app.id}
              app={app}
              narrow
              config={config}
              sessionRecord={currentSession}
              historyLoaded={historyLoaded}
              historyError={historyError}
              refresh={refresh}
              sessionId={sessionId}
              bindingId={url.searchParams.get('binding')}
              startupKey={url.searchParams.get('new') ?? 'default'}
              onNew={() =>
                app.id === 'chronicle'
                  ? navigate(`/apps/${app.id}?new=${crypto.randomUUID()}`)
                  : void launch(app, true)
              }
              launching={!!launching}
              error={
                error || (app.launchMode === 'direct' ? '' : settingsError)
              }
              onSessionCreated={(record) => {
                setSessionRecord(record)
                setSessions((items) => [
                  record,
                  ...items.filter((item) => item.id !== record.id),
                ])
                navigate(`/apps/${record.app_id}/sessions/${record.id}`)
              }}
              onSessionTitleChanged={updateSessionTitle}
            />
          ) : appId && sessionId && missingCatalogApp ? (
            <div className="m-screen">
              <TopBar title="Session" onBack={() => navigateBack('/')} />
              <div className="app-space-empty">
                {linkedError ? (
                  <>
                    <h2>This session is not available.</h2>
                    <p role="alert">{linkedError}</p>
                    <button className="btn" onClick={() => navigate('/')}>
                      Back to agents
                    </button>
                  </>
                ) : (
                  <OpeningState
                    className="opening-state--compact"
                    title="Opening shared session"
                    description="Checking its agent and access details."
                  />
                )}
              </div>
            </div>
          ) : appId && catalogLoading ? (
            <div className="m-screen">
              <TopBar title="Canopy" onBack={() => navigateBack('/')} />
              <OpeningState
                title={sessionId ? 'Loading session' : 'Loading agent'}
                description="Finding the agent in your workspace."
              />
            </div>
          ) : appId ? (
            <div className="m-screen">
              <TopBar title="Canopy" onBack={() => navigateBack('/')} />
              <div className="app-space-empty">
                <h2>This agent isn’t available in your workspace.</h2>
                <button className="btn" onClick={() => navigate('/')}>
                  Back to agents
                </button>
              </div>
            </div>
          ) : appId && !app && !sessionId && !catalogLoading ? (
            <div className="app-space-empty">
              <h2>That agent isn’t available in this workspace.</h2>
              <p role="alert">
                There is no agent called “{appId}” in{' '}
                {identity.state?.current_org?.name ?? 'this organization'}.
              </p>
              <button className="btn" onClick={() => navigate('/')}>
                Back to agents
              </button>
            </div>
          ) : (
            <MobileHome
              apps={apps}
              loading={catalogLoading}
              launching={launching}
              error={error || historyError}
              settingsOpen={screen === 'settings'}
              onCloseSettings={() => navigateBack('/')}
              onRetry={() => {
                void refreshApps()
                void refresh()
              }}
              onLaunch={(item) =>
                navigate(
                  `/apps/${item.id}${item.id === 'chronicle' ? `?new=${crypto.randomUUID()}` : ''}`,
                )
              }
            />
          )}
        </div>
      ) : (
        <div className={`app-space ${historyOpen ? 'history-open' : ''}`}>
          <Sidebar
            app={app}
            collapsed={historyCollapsed}
            apps={apps}
            sessions={sessions}
            selectedId={sessionId}
            archived={archived}
            onToggleArchived={toggleArchived}
            onChanged={refresh}
            hasMoreSessions={
              loadedHistoryMode.current === archived && !!sessionCursor
            }
            loadingMoreSessions={loadingMoreSessions}
            onLoadMoreSessions={loadMoreSessions}
            onSelect={select}
            onNew={() => {
              setHistoryOpen(false)
              navigate(
                app
                  ? `/apps/${app.id}${app.id === 'chronicle' ? `?new=${crypto.randomUUID()}` : ''}`
                  : '/',
              )
            }}
            onApp={(id) => {
              setHistoryOpen(false)
              navigate(
                id
                  ? `/apps/${id}${id === 'chronicle' ? `?new=${crypto.randomUUID()}` : ''}`
                  : '/',
              )
            }}
            onRenamed={(record) =>
              setSessions((items) =>
                items.map((item) => (item.id === record.id ? record : item)),
              )
            }
            onHome={() => {
              setHistoryOpen(false)
              navigate('/')
            }}
            refreshing={historyRefreshing}
            config={config}
            configError={historyError || null}
            busy={!!launching}
          />
          <div className="app-space-main">
            {url.searchParams.has('connections') ? (
              <GitHubConnections
                userId={
                  url.searchParams.get('connections') === 'me'
                    ? undefined
                    : url.searchParams.get('connections') || undefined
                }
              />
            ) : appId && app?.launchMode === 'overview' ? (
              <AppOverview app={app} />
            ) : appId && app ? (
              <AppSpace
                key={app.id}
                app={app}
                config={config}
                sessionRecord={currentSession}
                historyLoaded={historyLoaded}
                historyError={historyError}
                refresh={refresh}
                sessionId={sessionId}
                bindingId={url.searchParams.get('binding')}
                startupKey={url.searchParams.get('new') ?? 'default'}
                onNew={() =>
                  app.id === 'chronicle'
                    ? navigate(`/apps/${app.id}?new=${crypto.randomUUID()}`)
                    : void launch(app, true)
                }
                launching={!!launching}
                error={
                  error || (app.launchMode === 'direct' ? '' : settingsError)
                }
                onSessionCreated={(record) => {
                  setHistoryOpen(false)
                  setSessionRecord(record)
                  setSessions((items) => [
                    record,
                    ...items.filter((item) => item.id !== record.id),
                  ])
                  navigate(`/apps/${record.app_id}/sessions/${record.id}`)
                }}
                onSessionTitleChanged={updateSessionTitle}
              />
            ) : appId && sessionId && missingCatalogApp ? (
              <div className="app-space-empty">
                {linkedError ? (
                  <>
                    <h2>This session is not available.</h2>
                    <p role="alert">{linkedError}</p>
                    <button className="btn" onClick={() => navigate('/')}>
                      Back to agents
                    </button>
                  </>
                ) : (
                  <OpeningState
                    className="opening-state--compact"
                    title="Opening shared session"
                    description="Checking its agent and access details."
                  />
                )}
              </div>
            ) : appId && !app && !sessionId && !catalogLoading ? (
              <div className="app-space-empty">
                <h2>That agent isn’t available in this workspace.</h2>
                <p role="alert">
                  There is no agent called “{appId}” in{' '}
                  {identity.state?.current_org?.name ?? 'this organization'}.
                </p>
                <button className="btn" onClick={() => navigate('/')}>
                  Back to agents
                </button>
              </div>
            ) : (
              <AppHome
                apps={apps}
                loading={catalogLoading}
                launching={launching}
                error={error || historyError}
                onRetry={() => {
                  void refreshApps()
                  void refresh()
                }}
                onLaunch={(item) =>
                  navigate(
                    `/apps/${item.id}${item.id === 'chronicle' ? `?new=${crypto.randomUUID()}` : ''}`,
                  )
                }
              />
            )}
          </div>
        </div>
      )}
    </ConfigContext.Provider>
  )
}

function AppHome({
  apps,
  loading,
  launching,
  error,
  onRetry,
  onLaunch,
}: {
  apps: AppDefinition[]
  loading: boolean
  launching: string | null
  error: string
  onRetry: () => void
  onLaunch: (app: AppDefinition) => void
}) {
  return (
    <main className="app-home">
      <header className="home-topbar">
        <a href="/" className="home-brand">
          <CanopyMark />
          Canopy
        </a>
      </header>
      <section className="home-content" aria-label="Canopy agents">
        {loading ? (
          <GallerySkeleton />
        ) : (
          <GalleryGrid apps={apps} launching={launching} onLaunch={onLaunch} />
        )}
        {!loading && !apps.length && !error && (
          <p className="home-feedback">
            No agents are available in this organization yet.
          </p>
        )}
        {launching && (
          <p role="status" className="home-feedback">
            Opening {apps.find((item) => item.id === launching)?.title}…
          </p>
        )}
        {error && (
          <div className="home-error" role="alert">
            <p>{error}</p>
            <button className="btn" onClick={onRetry}>
              Try again
            </button>
          </div>
        )}
      </section>
    </main>
  )
}

function AppSpace({
  app,
  narrow = false,
  config,
  sessionRecord,
  historyLoaded,
  historyError,
  refresh,
  sessionId,
  bindingId,
  startupKey,
  onNew,
  launching,
  error,
  onSessionCreated,
  onSessionTitleChanged,
}: {
  app: AppDefinition
  narrow?: boolean
  config: BridgeConfig | null
  sessionRecord: SessionRecord | null
  historyLoaded: boolean
  historyError: string
  refresh: () => Promise<void>
  sessionId: string | null
  bindingId: string | null
  startupKey: string
  onNew: () => void
  launching: boolean
  error: string
  onSessionCreated: (record: SessionRecord) => void
  onSessionTitleChanged: (record: SessionRecord) => void
}) {
  const [dialog, setDialog] = useState<{
    preset?: Partial<SessionConfig>
  } | null>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const pendingHost = useRef<HTMLElement>(null)
  const identity = useIdentity()
  const direct = app.launchMode === 'direct'
  const implicit =
    !direct && appConfiguration(app).sessions.nature === 'implicit'
  const active = useRef(true)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  const current =
    sessionRecord?.id === sessionId && sessionRecord.app_id === app.id
      ? sessionRecord
      : null
  const [startingSession, setStartingSession] = useState<{
    id: string
    start: () => Promise<void>
    error: string
  } | null>(null)
  const finishStartup = async (id: string, start: () => Promise<void>) => {
    setStartingSession({ id, start, error: '' })
    try {
      await start()
      await refresh()
      if (active.current)
        setStartingSession((pending) => (pending?.id === id ? null : pending))
    } catch (failure) {
      getSessionStore(id).reportError((failure as Error).message, 'startup')
      if (active.current)
        setStartingSession((pending) =>
          pending?.id === id
            ? { id, start, error: (failure as Error).message }
            : pending,
        )
    }
  }
  const focusStartingSession = (
    record: SessionRecord,
    start: () => Promise<void>,
  ) => {
    void finishStartup(record.id, start)
    onSessionCreated(record)
  }
  const startupSteps = app.descriptor?.startup?.steps ?? []
  const appProvider =
    app.descriptor?.agent?.providerPreference ??
    app.descriptor?.agentPreference?.provider
  const promptFirstAgentLaunch =
    !!appProvider &&
    app.launchMode === 'chat' &&
    startupSteps.length <= 1 &&
    startupSteps.every(
      (step) => !step.action && (step.fields?.length ?? 0) === 0,
    )
  const select = (id: string) => {
    if (active.current) navigate(`/apps/${app.id}/sessions/${id}`)
  }
  const settingUp =
    !!current &&
    current.status === 'created' &&
    current.migration_status !== 'diagnostic' &&
    !!config
  // The session screen draws its own header; every other app view gets the app's.
  const appBar = narrow && !(sessionId && current && !settingUp && !direct)
  return (
    <>
      <main
        ref={pendingHost}
        className={`app-space-main ${narrow ? 'm-screen' : ''}`}
      >
        {appBar && (
          <TopBar
            title={app.title}
            subtitle={
              sessionId && settingUp ? 'Session setup' : app.description
            }
            left={
              <img
                className="m-topbar-app-icon"
                src={appIcon(app.iconUrl, 64)}
                alt=""
              />
            }
            onBack={() => navigateBack('/')}
            right={
              <>
                {!direct && (
                  <CircleButton
                    icon="clock"
                    tone="ghost"
                    label="Sessions"
                    onClick={() => navigate(`/sessions?app=${app.id}`)}
                  />
                )}
                <CircleButton
                  icon="grid"
                  tone="ghost"
                  label="All agents"
                  onClick={() => navigate('/')}
                />
              </>
            }
          />
        )}
        {(error || (!direct && historyError)) && (
          <p className="app-space-error" role="alert">
            {error || historyError}
          </p>
        )}
        {direct ? (
          <DirectAppOpening
            key={`${app.id}:${app.digest}:${identity.state?.current_org?.id}`}
            app={app}
          />
        ) : sessionId ? (
          !historyLoaded ? (
            <OpeningState
              title="Loading saved conversation"
              description="Finding the latest session details."
              error={historyError}
              onRetry={historyError ? () => void refresh() : undefined}
            />
          ) : current?.status === 'created' &&
            startingSession?.id !== sessionId &&
            current.migration_status !== 'diagnostic' &&
            config ? (
            <AppStartup
              key={`${current.id}:${current.config.startup_step}`}
              pendingHost={pendingHost}
              narrow={narrow}
              app={app}
              config={config}
              record={current}
              onCreated={onSessionCreated}
              onStarting={focusStartingSession}
              onChanged={() => void refresh()}
            />
          ) : current ? (
            <SessionView
              app={app}
              key={sessionId}
              narrow={narrow}
              sessionId={sessionId}
              record={current}
              infoOpen={infoOpen}
              onToggleInfo={() => setInfoOpen((value) => !value)}
              onSessionsChanged={refresh}
              onClosed={() => {
                if (active.current) navigate(`/apps/${app.id}`)
              }}
              onFork={(preset) => setDialog({ preset })}
              onSessionTitleChanged={onSessionTitleChanged}
            />
          ) : historyError ? (
            <div className="app-space-empty">
              <h2>This session isn’t available in {app.title}.</h2>
              <p role="alert">{historyError}</p>
              <button
                className="btn"
                onClick={() => navigate(`/apps/${app.id}`)}
              >
                Back to this app
              </button>
            </div>
          ) : (
            <OpeningState
              title="Loading saved conversation"
              description="Finding the latest session details."
            />
          )
        ) : bindingId && app.launchMode === 'workspace' ? (
          <BoundWorkspace
            key={bindingId}
            app={app}
            config={config}
            bindingId={bindingId}
            onConnected={select}
          />
        ) : implicit &&
          appConfiguration(app).ui.mode === 'ui' &&
          !app.descriptor?.startup?.steps?.length ? (
          historyLoaded && !historyError ? (
            <ImplicitAppOpening
              key={app.id}
              app={app}
              onCreated={onSessionCreated}
            />
          ) : (
            <OpeningState
              title={`Opening ${app.title}`}
              description="Getting the saved workspace ready."
              error={historyError}
              onRetry={historyError ? () => void refresh() : undefined}
            />
          )
        ) : config && promptFirstAgentLaunch ? (
          <NewSessionDialog
            pendingHost={pendingHost}
            app={app}
            config={config}
            presentation="pane"
            narrow={narrow}
            provider={app.id === 'loop-engineering' ? null : appProvider}
            preset={{
              ...app.descriptor?.startup?.defaults,
              ...(app.configuration
                ?.session_defaults as Partial<SessionConfig>),
              app_id: app.id,
              package_digest: app.digest,
              project: '',
            }}
            onClose={() => navigate('/')}
            onCreated={onSessionCreated}
          />
        ) : config ? (
          <AppStartup
            key={`${app.id}:${startupKey}`}
            pendingHost={pendingHost}
            narrow={narrow}
            app={app}
            config={config}
            startupKey={startupKey}
            onCreated={onSessionCreated}
            onStarting={focusStartingSession}
            onChanged={() => void refresh()}
          />
        ) : (
          <div className="app-space-empty">
            <img
              className="app-space-icon"
              src={appIcon(app.iconUrl, 128)}
              width={64}
              height={64}
              decoding="async"
              alt=""
            />
            <h1>{app.title}</h1>
            <p>{app.description}</p>
            <button
              className="btn btn-primary"
              onClick={onNew}
              disabled={launching}
            >
              {launching
                ? 'Opening…'
                : app.launchMode === 'chat'
                  ? 'Start a blank session'
                  : 'Open a new workspace'}
            </button>
            {app.launchMode === 'chat' && (
              <button
                className="btn"
                onClick={() =>
                  setDialog({ preset: { app_id: app.id, project: '' } })
                }
                disabled={!config}
              >
                Choose a project and settings
              </button>
            )}
          </div>
        )}
      </main>
      {dialog && config && (
        <NewSessionDialog
          narrow={narrow}
          pendingHost={pendingHost}
          app={app}
          config={config}
          provider={
            dialog.preset?.provider ?? app.descriptor?.agentPreference?.provider
          }
          preset={{ ...dialog.preset, app_id: app.id }}
          onClose={() => setDialog(null)}
          onCreated={(record) => {
            setDialog(null)
            onSessionCreated(record)
          }}
        />
      )}
    </>
  )
}

/** Load a trusted host app independently of session history and runner availability. */
function DirectAppOpening({ app }: { app: AppDefinition }) {
  const [binding, setBinding] = useState<AppBinding | null>(null)
  const [failure, setFailure] = useState('')
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    let active = true
    void api
      .openApp(app.id)
      .then((result) => {
        if (!result.binding) throw new Error('Could not open this workspace.')
        if (active) setBinding(result.binding)
      })
      .catch((error: Error) => {
        if (active) setFailure(error.message)
      })
    return () => {
      active = false
    }
  }, [app.id, attempt])
  return (
    <div className="app-bound-surface">
      <div className="app-bound-workspace app-direct-workspace">
        <header className="app-workspace-heading">
          <h1>{app.title}</h1>
        </header>
        {failure ? (
          <div className="app-space-error" role="alert">
            {failure}
            <button
              type="button"
              className="btn"
              onClick={() => {
                setFailure('')
                setAttempt(attempt + 1)
              }}
            >
              Try again
            </button>
          </div>
        ) : binding ? (
          <AppPane app={app} bindingId={binding.id} sessionId={null} />
        ) : (
          <OpeningState
            className="opening-state--direct"
            title={`Opening ${app.title}`}
            description="Preparing your workspace."
            illustration={
              <img
                className="opening-state-app-icon"
                src={appIcon(app.iconUrl, 64)}
                width={40}
                height={40}
                decoding="async"
                alt=""
              />
            }
          />
        )}
      </div>
    </div>
  )
}

/** Open a legacy implicit workspace with its associated conversation. */
function ImplicitAppOpening({
  app,
  onCreated,
}: {
  app: AppDefinition
  onCreated: (record: SessionRecord) => void
}) {
  const pending = useRef<ReturnType<typeof api.launchApp> | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [failure, setFailure] = useState('')
  useEffect(() => {
    let active = true
    if (!pending.current) {
      beginLaunch('implicit-app')
      pending.current = api.launchApp(app.id, {
        packageDigest: app.digest,
        implicit: true,
      })
    }
    void pending.current
      .then((result) => {
        if (!result.session) throw new Error('Could not open this workspace.')
        markLaunch('create_response')
        adoptLaunch(result.session.id)
        if (active) onCreated(result.session)
      })
      .catch((error: Error) => {
        if (active) setFailure(error.message)
      })
    return () => {
      active = false
    }
  }, [app.id, app.digest, attempt, onCreated])
  return (
    <div className="startup-surface">
      <OpeningState
        title={`Opening ${app.title}`}
        description="Preparing its saved workspace."
        error={failure}
        illustration={
          <img
            className="app-space-icon"
            src={appIcon(app.iconUrl, 128)}
            width={64}
            height={64}
            decoding="async"
            alt=""
          />
        }
        onRetry={() => {
          pending.current = null
          setFailure('')
          setAttempt((value) => value + 1)
        }}
      />
      <footer
        className="session-status chrome"
        role="contentinfo"
        aria-label="Session status bar"
      >
        <span className="session-lifecycle">Opening</span>
        <SessionCost total={0} />
      </footer>
    </div>
  )
}

function BoundWorkspace({
  app,
  config,
  bindingId,
  onConnected,
}: {
  app: AppDefinition
  bindingId: string
  onConnected: (id: string) => void
  config: BridgeConfig | null
}) {
  const [binding, setBinding] = useState<AppBinding | null>(null)
  const [settings, setSettings] = useState<Partial<SessionConfig>>(
    config?.defaults ?? {},
  )
  const selection = config ? launchSelection(config, settings) : null
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  useEffect(() => {
    let active = true
    setBinding(null)
    setError('')
    void api
      .appBinding(bindingId)
      .then((value) => {
        if (value.appId !== app.id)
          throw new Error('This workspace belongs to another app.')
        if (active) {
          setBinding(value)
          if (value.session) onConnected(value.session)
        }
      })
      .catch((error) => {
        if (active) setError(error.message)
      })
    return () => {
      active = false
    }
  }, [bindingId, app.id, loadAttempt])
  const connect = async () => {
    if (!binding) return
    setBusy(true)
    setError('')
    try {
      const record = await api.connectApp(
        binding.id,
        launchSettings({
          ...settings,
          provider: selection?.provider?.id,
          execution_mode: selection?.mode?.id,
          environment: selection?.environment,
        }),
      )
      onConnected(record.id)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="app-bound-surface">
      <div className="app-bound-workspace">
        <header className="app-workspace-heading">
          <div>
            <h1>{app.title}</h1>
            <p>Your changes save as you go.</p>
          </div>
          <div className="app-conversation-controls">
            {!binding?.session && config && (
              <ProviderSettings
                config={config}
                settings={settings}
                onChange={(patch) =>
                  setSettings((current) => ({ ...current, ...patch }))
                }
                disabled={busy}
              />
            )}
            <button
              className="btn"
              disabled={
                busy || !binding || (!binding.session && !selection?.available)
              }
              onClick={() => void connect()}
            >
              {busy
                ? 'Connecting…'
                : binding?.session
                  ? 'Open conversation'
                  : 'Start a conversation'}
            </button>
          </div>
        </header>
        {!binding && (
          <OpeningState
            title={`Opening ${app.title}`}
            description="Checking the saved workspace."
            error={error}
            onRetry={
              error
                ? () => {
                    setError('')
                    setLoadAttempt((value) => value + 1)
                  }
                : undefined
            }
          />
        )}
        {binding && error && (
          <p role="alert" className="app-space-error">
            {error}
          </p>
        )}
        {binding && (
          <AppPane
            app={app}
            key={binding.id}
            bindingId={binding.id}
            sessionId={binding.session}
          />
        )}
      </div>
      <footer
        className="session-status chrome"
        role="contentinfo"
        aria-label="Session status bar"
      >
        <span className="session-lifecycle">Workspace</span>
        <SessionCost sessionId={binding?.session} />
      </footer>
    </div>
  )
}
